import { IAccessControl } from '../acl/types';
import { IApiUser, IUserName } from '../users/types';
import {
  AwardStatus,
  BuyerType,
  JunctionLogic,
  Keyword,
  ProfileType,
  SearchLogic,
  SupplierSearchType
} from '@tendium/prom-types';
import { ApiTendersVars } from '../procurements/Tenders/types';
import isObject from 'src/helpers/isObject';

export interface BTag {
  id: string;
  name: string;
  description: string;
}

export type MpCode = {
  code: string;
  description: string;
  children: MpCode[];
};
export type MpCodeTypes = 'cpvCodes' | 'nutsCodes';
export type CpvCodesResponse = { searchCpvCodes: MpCode[] };
export type NutsCodesResponse = { searchNutCodes: MpCode[] };
export function isCpvCodesResponse(u: unknown): u is CpvCodesResponse {
  return isObject(u) && 'searchCpvCodes' in u;
}

export interface CodeFilter {
  codes: string[];
  codesExact: string[];
}

export interface IApiMp extends IMpValues {
  id: string;
  name: string;
  owners: IApiUser[];
  subscribers: IApiUser[];
  accessToEdit: boolean;
  accessControl: IAccessControl;
  profileType: ProfileType;
}

export interface IMatchingProfile extends Omit<IApiMp, 'owners' | 'subscribers'> {
  owners: IUserName[];
  subscribers: IUserName[];
}

/** @deprecated use MpKeyword */
export interface IKeyword extends MpKeywordSettings {
  id: string;
  value: string;
}
export type MpKeywordSettings = {
  searchType: SearchType;
  searchLogic: SearchLogic;
  filterLogic: FilterLogic;
  highlightColor: HighlightColor;
};
export type MpKeyword = MpKeywordSettings & {
  id: string;
  value: string;
};
export type MpNewKeyword = Keyword;

/** @deprecated use to MpNewKeyword */
export interface NewKeyword {
  value: string;
  searchType?: SearchType;
  searchLogic?: SearchLogic;
  filterLogic?: FilterLogic;
  highlightColor?: HighlightColor;
}
export type ContractValueRange = {
  currency?: string;
  min?: number;
  max?: number;
};
export type MpContractValue = ContractValueRange & { includeMissingValue?: boolean };

export interface IMpValues {
  nutsCodes?: string[];
  nutsCodesExact?: string[];
  cpvCodes?: string[];
  cpvCodesExact?: string[];
  bTagIds?: string[];
  keywords?: IKeyword[];
  buyerOrgIds?: string[];
  buyerSearchStrings?: string[];
  supplierSearchTerms?: SupplierSearchTerm[];
  supplierSearchLogic?: JunctionLogic;
  contractValueCurrency?: string;
  contractValueMax?: number;
  contractValueMin?: number;
  contractValueIncludeMissingValue?: boolean;
  buyerTypes?: BuyerType[];
}

export interface IMpSidebarData {
  isMPUpdated: boolean;
  resetMPData: (variables?: ApiTendersVars) => void;
}
export interface SupplierSearchTerm {
  id: string;
  searchType: SupplierSearchType;
  status: AwardStatus;
  value: string;
}
export enum FilterLogic {
  BroadSearch = 'BroadSearch',
  NarrowSearch = 'NarrowSearch',
  HighlightOnly = 'HighlightOnly'
}

export enum SearchType {
  Notice = 'Notice',
  Document = 'Document',
  NoticeAndDocument = 'NoticeAndDocument'
}

export enum HighlightColorDict {
  Yellow = '#FFFB8F',
  Green = '#D9F7BE',
  Red = '#FFCCC7'
}

export enum HighlightColor {
  Yellow = 'Yellow',
  Green = 'Green',
  Red = 'Red'
}
export const DEFAULT_KEYWORD_SETTING: MpKeywordSettings = {
  searchType: SearchType.Notice,
  searchLogic: SearchLogic.PrefixMatch,
  filterLogic: FilterLogic.BroadSearch,
  highlightColor: HighlightColor.Yellow
};

export const DOCUMENTS_SEARCH_TYPE = [SearchType.Document, SearchType.NoticeAndDocument];
export const SEARCH_TYPES = [...DOCUMENTS_SEARCH_TYPE, SearchType.Notice];
export const ADVANCED_FILTER_LOGIC = [FilterLogic.BroadSearch, FilterLogic.NarrowSearch];
export const PREFIX_SEARCH_LIMIT = 20;

export interface IMpSnapshot {
  initial?: IMpSnapshotValues;
  isUpdated: boolean;
}
export type IMpSnapshotValues = IMpValues & { id?: string };

export const INIT_SNAPSHOT: IMpSnapshot = {
  initial: undefined,
  isUpdated: false
};

export const COMPANY_FILTER_LIMIT = 100;

export const KEYWORD_LIMIT = 300;
export const SELECTABLE_LOGIC = [SearchLogic.ExactMatch, SearchLogic.PrefixMatch];
